import { Injectable } from '@angular/core';
import { FinishedRetreadPartSummaryReport, RepairPartSummaryReport, RejectPartSummaryReport, TableRow } from '../models/pack-list.model';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';

export interface PackListPdfContext {
  packListNumber: string;
  userLocationCode: string;
  destinationType: string;
  destinationName: string;
  userLogin: string;
  sourceName: string;
  sourceType: string;
  generationDate: string;
  totalCasings: number;
  retreadCount: number;
  rejectCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class PackListPdfExportService {
  protected createDoc(): jsPDF {
    return new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  }

  protected applyAutoTable(doc: jsPDF, options: any): void {
    autoTable(doc, options);
  }

  generatePackListPdf(
    context: PackListPdfContext,
    finishedRetreadPartSummary: FinishedRetreadPartSummaryReport[],
    repairPartSummary: RepairPartSummaryReport[],
    rejectPartSummary: RejectPartSummaryReport[],
    lines: TableRow[]
  ): void {
    // Page setup: A4 landscape with margins matching iText Document(PageSize.A4.rotate(), 24, 24, 20+100, 36)
    // 24pt ≈ 8.5mm (left/right), 120pt ≈ 42.3mm (top: 20pt margin + 100pt header), 36pt ≈ 12.7mm (bottom)
    const marginLeft = 8.5;
    const marginRight = 8.5;
    const marginTop = 7; // ~20pt — top page margin where header starts
    const headerHeight = 35.3; // ~100pt — reserved header area
    const marginBottom = 12.7; // ~36pt

    const doc = this.createDoc();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const lineHeight = 6;
    const y = marginTop;

    const contentWidth = pageWidth - marginLeft - marginRight; // ~280mm
    const sectionWidth = contentWidth / 3; // each section ~70mm

    // --- Part 1: Totals Section ---
    const centerX = pageWidth / 2;
    doc.setFontSize(9);
    doc.setFont('helvetica');
    doc.text(`Casing Total: ${context.totalCasings}`, centerX, y + lineHeight * 5, { align: 'center' });
    doc.setFontSize(7);
    doc.text(`Finished Retread: ${context.retreadCount}`, centerX, y + lineHeight * 6, { align: 'center' });
    doc.text(`Reject Total: ${context.rejectCount}`, centerX, y + lineHeight * 7, { align: 'center' });

    // --- Part 2: Packlist Info Section ---
    doc.setFontSize(9);
    doc.text(`For ${context.destinationName}`, centerX, y, { align: 'center' });
    doc.text(`${context.packListNumber}`, centerX, y + lineHeight, { align: 'center' });

    // --- Part 3: Created By Section ---
    const createdByX = marginLeft + sectionWidth * 2;
    doc.setFontSize(7);
    doc.text(`Created By: ${context.userLogin}`, createdByX, y + lineHeight * 2);
    doc.text(`Created Date: ${context.generationDate}`, createdByX, y + lineHeight * 3);

    // --- Part 5: By Section ---
    const ByX = marginLeft;
    doc.setFontSize(7);
    doc.text(`By: ${context.sourceName}`, ByX, y + lineHeight * 2);

    const separatorY = marginTop + headerHeight;

    // Main Casing Table
    const mainColumns = [
      { header: 'Cust #', dataKey: 'customerCode' },
      { header: 'Cust Name', dataKey: 'customerName' },
      { header: 'CRN #', dataKey: 'crnNumber' },
      { header: 'Barcode', dataKey: 'barCode' },
      { header: 'Work Order', dataKey: 'workOrder' },
      { header: 'Line #', dataKey: 'lineNumber' },
      { header: 'PC', dataKey: 'processCode' },
      { header: 'Make', dataKey: 'make' },
      { header: 'Series', dataKey: 'series' },
      { header: 'Size', dataKey: 'size' },
      { header: 'Tread', dataKey: 'tread' },
      { header: 'Width', dataKey: 'width' },
      { header: 'Brand #', dataKey: 'brandNumber' },
      { header: 'Rej. Code', dataKey: 'rejectCode' },
      { header: 'Rej. Desc', dataKey: 'rejectDescription' },
      { header: 'Dispos. Code', dataKey: 'disposition' }
    ];
    const mainTableData = lines.map((line) => mainColumns.map((c) => (line as any)[c.dataKey] ?? ''));
    this.applyAutoTable(doc, {
      startY: separatorY + 10,
      head: [
        [
          {
            content: 'Main Casing Table',
            colSpan: mainColumns.length,
            styles: {
              halign: 'center',
              fontStyle: 'bold',
              fontSize: 9,
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              lineColor: [0, 0, 0],
              lineWidth: 0.4
            }
          }
        ],
        mainColumns.map((col) => col.header)
      ],
      body: mainTableData,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5, fontStyle: 'bold', textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.4 },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 7,
        lineColor: [0, 0, 0],
        lineWidth: 0.4
      },
      margin: { left: marginLeft, right: marginRight, bottom: marginBottom + 5 }
    });

    // Finished Retread Part Summary Table
    const frColumns = [
      { header: 'Count', dataKey: 'count' },
      { header: 'Part', dataKey: 'partNumber' },
      { header: 'Description', dataKey: 'description' }
    ];
    const frTableData = finishedRetreadPartSummary.map((line) => frColumns.map((c) => (line as any)[c.dataKey] || ''));
    this.applyAutoTable(doc, {
      startY: (doc as any).lastAutoTable.finalY + 5,
      head: [
        [
          {
            content: 'Finished Retread Part Summary',
            colSpan: frColumns.length,
            styles: {
              halign: 'center',
              fontStyle: 'bold',
              fontSize: 9,
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              lineColor: [0, 0, 0],
              lineWidth: 0.4
            }
          }
        ],
        frColumns.map((col) => col.header)
      ],
      body: frTableData,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5, fontStyle: 'bold', textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.4 },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 7,
        lineColor: [0, 0, 0],
        lineWidth: 0.4
      },
      margin: { left: marginLeft, right: marginRight, bottom: marginBottom + 5 }
    });

    // Repair Part Summary Table
    const frTableFinalY = (doc as any).lastAutoTable.finalY;
    const rpColumns = [
      { header: 'Count', dataKey: 'count' },
      { header: 'Part', dataKey: 'partNumber' },
      { header: 'Repair Code', dataKey: 'repairCode' },
      { header: 'Repair Description', dataKey: 'repairDescription' }
    ];
    const rpTableData = repairPartSummary.map((line) => rpColumns.map((c) => (line as any)[c.dataKey] || ''));
    this.applyAutoTable(doc, {
      startY: frTableFinalY + 5,
      head: [
        [
          {
            content: 'Repair Part Summary',
            colSpan: rpColumns.length,
            styles: {
              halign: 'center',
              fontStyle: 'bold',
              fontSize: 9,
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              lineColor: [0, 0, 0],
              lineWidth: 0.4
            }
          }
        ],
        rpColumns.map((col) => col.header)
      ],
      body: rpTableData,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5, fontStyle: 'bold', textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.4 },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 7,
        lineColor: [0, 0, 0],
        lineWidth: 0.4
      },
      margin: { left: marginLeft, right: marginRight, bottom: marginBottom + 5 }
    });

    // Reject Part Summary Table
    const rpTableFinalY = (doc as any).lastAutoTable.finalY;
    const rjColumns = [
      { header: 'Count', dataKey: 'count' },
      { header: 'Part', dataKey: 'partNumber' },
      { header: 'Reject Code', dataKey: 'rejectCode' },
      { header: 'Reject Description', dataKey: 'rejectDescription' }
    ];
    const rjTableData = rejectPartSummary.map((line) => rjColumns.map((c) => (line as any)[c.dataKey] || ''));
    this.applyAutoTable(doc, {
      startY: rpTableFinalY + 5,
      head: [
        [
          {
            content: 'Reject Part Summary',
            colSpan: rjColumns.length,
            styles: {
              halign: 'center',
              fontStyle: 'bold',
              fontSize: 9,
              fillColor: [255, 255, 255],
              textColor: [0, 0, 0],
              lineColor: [0, 0, 0],
              lineWidth: 0.4
            }
          }
        ],
        rjColumns.map((col) => col.header)
      ],
      body: rjTableData,
      theme: 'grid',
      styles: { fontSize: 7, cellPadding: 1.5, fontStyle: 'bold', textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.4 },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 7,
        lineColor: [0, 0, 0],
        lineWidth: 0.4
      },
      margin: { left: marginLeft, right: marginRight, bottom: marginBottom + 5 }
    });

    //Footer
    const pageCount = doc.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text(`Generated: ${new Date().toLocaleString()}`, marginLeft, pageHeight - marginBottom + 3);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - marginRight - 20, pageHeight - marginBottom + 3);
    }

    doc.save(`PackList_${context.packListNumber || 'export'}.pdf`);
  }
}
